<script setup>
import { AppIcon, CornerBrackets } from '@/components/shared'
import { home as homeRoute } from '@/routes'

/**
 * Auth-page shell with a left-column atmospheric hero + right-column form
 * slot. Used by Login, Register, ForgotPassword, ResetPassword,
 * ConfirmPassword.
 *
 * `heroImage` accepts a path to an optimized WebP (see public/inspirations/
 * for the set). Each auth page passes its own image so the user gets a
 * fresh visual on each step instead of the same shot recycled. Falls back
 * to a generic inspirations shot when not specified.
 *
 * Both the desktop hero logo + a dedicated mobile back-link route the user
 * to the homepage - otherwise a visitor who lands directly on /login has
 * no way out without the browser back button.
 */
defineProps({
    heroTitle: {
        type: String,
        default: 'Zachytíme',
    },
    heroAccent: {
        type: String,
        default: 'každý náklon.',
    },
    heroCopy: {
        type: String,
        default:
            'Profesionální platforma pro motocyklové fotografy a jezdce. Přístup k vašim galeriím v nejvyšším rozlišení.',
    },
    heroImage: {
        type: String,
        default: '/inspirations/auth-default.webp',
    },
    heroImageAlt: {
        type: String,
        default: 'Motocyklový jezdec na české horské cestě',
    },
})
</script>

<template>
    <main class="bg-background text-on-surface grid min-h-screen grid-cols-1 md:grid-cols-2">
        <!-- Left: Atmospheric Visual. Each auth page passes its own hero so
             Login/Register/ForgotPassword get distinct visuals - gives the
             flow a feel of momentum instead of looping the same photo.
             Corner brackets reinforce tactical brand language. -->
        <section class="relative hidden flex-col justify-between overflow-hidden p-12 md:flex">
            <div class="absolute inset-0 z-0">
                <img
                    class="h-full w-full object-cover brightness-[0.45]"
                    :alt="heroImageAlt"
                    :src="heroImage"
                    fetchpriority="high"
                />
                <div class="bg-primary-container/10 absolute inset-0 mix-blend-overlay" />
                <div
                    class="from-background absolute inset-0 bg-linear-to-t via-transparent to-transparent opacity-80"
                />
            </div>

            <CornerBrackets variant="hero" />

            <!--
                Logo is a Link to home - without it a visitor who lands on
                /login (e.g. from an email or external link) has no way to
                reach the rest of the site without typing the URL. group
                + hover ladders the orange bar that sits under the wordmark
                so it animates the same way as the navbar logo.
            -->
            <div class="relative z-10">
                <slot name="logo">
                    <Link
                        class="group inline-block"
                        :href="homeRoute.url()"
                        aria-label="Zpět na RideSnap homepage"
                    >
                        <p
                            class="text-primary-container mb-3 flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] uppercase"
                        >
                            <span class="bg-primary-container/80 inline-block h-px w-6"></span>
                            Komunita motofotografů
                        </p>
                        <div class="flex items-center gap-2">
                            <span
                                class="teko-font text-primary-container group-hover:text-primary text-5xl font-bold tracking-tighter uppercase transition-colors"
                            >
                                RideSnap
                            </span>
                        </div>
                        <div class="bg-primary-container mt-2 h-1 w-16 transition-all group-hover:w-24" />
                    </Link>
                </slot>
            </div>

            <!-- Hero copy. Was an <h2> but semantically this is decorative
                 brand copy, not a section heading - and rendering an <h2>
                 BEFORE the form slot's <h1> (Login/Register/etc.) violated
                 the document outline (h2 → h1 is a backwards skip). Demoted
                 to <p> with identical visual styling; the form's <h1> now
                 owns the page outline correctly. -->
            <div class="relative z-10 max-w-md">
                <slot name="hero">
                    <p class="teko-font text-on-surface text-6xl leading-none font-bold tracking-tight uppercase">
                        {{ heroTitle }}
                        <br />
                        <span class="text-primary-container">{{ heroAccent }}</span>
                    </p>
                    <p class="text-on-surface-variant mt-6 text-lg leading-relaxed font-medium">
                        {{ heroCopy }}
                    </p>
                </slot>
            </div>
        </section>

        <!-- Right: Form -->
        <section class="bg-surface-container-lowest relative flex flex-col items-center justify-center p-8 md:p-16">
            <!--
                Always-visible back-link to home. The desktop logo on the
                left hero column IS clickable but the visual cue (color
                shift on hover) is easy to miss; a dedicated arrow_back
                CTA in the form column gives users an unambiguous escape
                hatch regardless of viewport. Same arrow-+-mono-caps
                treatment used across breadcrumbs in Studio/Profile.
            -->
            <Link
                class="text-on-surface-variant hover:text-primary-container absolute top-6 left-6 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold tracking-widest uppercase transition-colors md:top-8 md:left-8"
                :href="homeRoute.url()"
            >
                <AppIcon
                    class="text-base"
                    name="arrow_back"
                />
                Zpět na RideSnap
            </Link>

            <div class="w-full max-w-md">
                <slot />
            </div>
        </section>
    </main>
</template>
